import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Seo } from "../components/site/Seo";
import { MaskLines, Eyebrow, Reveal } from "../components/site/Motion";
import { ArticleCard } from "../components/site/ArticleCard";
import { CTABand } from "../components/site/CTABand";
import { ARTICLES } from "../content/insights";

export default function ArticlePage() {
  const { slug } = useParams();
  const a = ARTICLES.find((x) => x.slug === slug);
  if (!a) return <Navigate to="/insights" replace />;
  const more = ARTICLES.filter((x) => x.slug !== slug).slice(0, 3);
  return (
    <>
      <Seo title={a.title} path={`/insights/${a.slug}`} description={a.excerpt} />
      <article className="bg-[#F9F8F3] pt-32 md:pt-40" data-testid="article-page">
        <div className="mx-auto max-w-4xl px-5 md:px-10">
          <Link to="/insights" className="inline-flex items-center gap-2 text-sm font-medium text-[#082619]" data-testid="article-back">
            <ArrowLeft className="h-4 w-4" /> All insights
          </Link>
          <div className="mt-10"><Eyebrow>{a.category} · {a.read}</Eyebrow></div>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-tight text-[#082619]">
            <MaskLines lines={[a.title]} />
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-[#4C6053]">{a.excerpt}</p>
        </div>
        <motion.div
          initial={{ clipPath: "inset(10% 10% 0% 10% round 24px)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 16px)" }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          className="mx-auto mt-14 aspect-[16/8] max-w-6xl overflow-hidden px-0 md:px-10"
        >
          <img src={a.image} alt="" className="h-full w-full rounded-2xl object-cover" />
        </motion.div>
        <div className="mx-auto max-w-3xl px-5 py-16 md:px-10 md:py-24">
          {a.body.map((b, i) => (
            <Reveal key={b.h} delay={0.03 * i} className="mb-12">
              <h2 className="font-display text-2xl md:text-3xl font-medium text-[#082619]">{b.h}</h2>
              <p className="mt-4 text-base md:text-lg leading-relaxed text-[#4C6053]">{b.p}</p>
            </Reveal>
          ))}
          <p className="rounded-xl bg-[#F2EFE7] p-5 text-sm leading-relaxed text-[#4C6053]" data-testid="article-disclaimer">
            This article is general information for educational purposes. It is not legal, tax, financial or regulatory advice. Regulations and charges vary by state and change over time; please evaluate your specific case with qualified advisors.
          </p>
        </div>
      </article>
      <section className="border-t border-[#E5E1D3] bg-[#F9F8F3] py-20" data-testid="article-more">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <h2 className="font-display text-3xl font-medium text-[#082619]">More insights</h2>
          <div className="mt-10 grid gap-10 md:grid-cols-3">{more.map((m, i) => <ArticleCard key={m.slug} a={m} i={i} />)}</div>
        </div>
      </section>
      <CTABand />
    </>
  );
}
